/**
 * SpaceCalculator - Precise Space Analysis for Intelligent Layout
 * 
 * This system calculates available space, overlap detection, and optimal positioning
 * for text elements within containers at different scales and breakpoints.
 * 
 * CRITICAL: Pure mathematical calculations - NO side effects
 */

import type {
  Rectangle,
  Dimensions,
  SmartElement,
  RenderQuality
} from './SmartLayout.types';

/**
 * Space analysis result
 */
export interface SpaceAnalysis {
  availableSpace: Rectangle;
  constraints: {
    maxWidth: number;
    maxHeight: number;
    padding: { top: number; right: number; bottom: number; left: number };
  };
  scaling: {
    globalScale: number;
    localScale: number; // Element-specific scale adjustments
    effectiveFontScale: number;
  };
  quality: {
    spaciousness: number;    // 0-1: How much room is available
    readability: number;     // 0-1: How readable text would be
    aesthetics: number;      // 0-1: How good the layout looks
  };
  recommendations: {
    optimalFontSize: number;
    maxLines: number;
    suggestedPadding: number;
  };
}

/**
 * Layout conflict detection
 */
export interface LayoutConflict {
  elementA: string;     // Element ID
  elementB: string;     // Element ID
  overlapArea: number;  // Pixels of overlap
  severity: 'minor' | 'major' | 'critical';
  resolution: 'resize' | 'reposition' | 'hide' | 'external-label';
}

export class SpaceCalculator {
  private static readonly FONT_METRICS = {
    'Space Grotesk': {
      widthRatio: 0.55,     // Character width as ratio of font size
      heightRatio: 1.2,     // Line height as ratio of font size
      ascenderRatio: 0.75,  // Ascender height ratio
      descenderRatio: 0.25  // Descender height ratio
    }
  };

  private static readonly QUALITY_THRESHOLDS = {
    spaciousness: {
      excellent: 0.8,
      good: 0.6,
      acceptable: 0.4,
      poor: 0.2
    },
    readability: {
      minFontSize: 8,
      comfortableFontSize: 12,
      optimalFontSize: 14
    }
  };

  /**
   * Analyze available space for an element at given scale
   */
  static analyzeSpace(
    element: SmartElement,
    containerDimensions: Dimensions,
    globalScale: number = 1,
    otherElements: SmartElement[] = []
  ): SpaceAnalysis {
    const scaledBounds = this.applyScale(element.bounds, globalScale);
    const effectivePadding = this.calculateEffectivePadding(element, globalScale);
    
    const availableSpace = {
      x: scaledBounds.x + effectivePadding.left,
      y: scaledBounds.y + effectivePadding.top,
      width: scaledBounds.width - effectivePadding.left - effectivePadding.right,
      height: scaledBounds.height - effectivePadding.top - effectivePadding.bottom
    };

    // Ensure minimum space
    availableSpace.width = Math.max(0, availableSpace.width);
    availableSpace.height = Math.max(0, availableSpace.height);

    const scaling = {
      globalScale,
      localScale: this.calculateLocalScale(element, availableSpace),
      effectiveFontScale: this.calculateFontScale(availableSpace, element.content.text || '')
    };

    const quality = this.assessSpaceQuality(availableSpace, element, scaling);
    const recommendations = this.generateRecommendations(availableSpace, element, scaling);

    return {
      availableSpace,
      constraints: {
        maxWidth: availableSpace.width,
        maxHeight: availableSpace.height,
        padding: effectivePadding
      },
      scaling,
      quality,
      recommendations
    };
  }

  /**
   * Detect layout conflicts between elements
   */
  static detectConflicts(
    elements: SmartElement[],
    globalScale: number = 1
  ): LayoutConflict[] {
    const conflicts: LayoutConflict[] = [];
    
    for (let i = 0; i < elements.length; i++) {
      for (let j = i + 1; j < elements.length; j++) {
        const conflict = this.checkElementConflict(elements[i], elements[j], globalScale);
        if (conflict) {
          conflicts.push(conflict);
        }
      }
    }

    return conflicts.sort((a, b) => {
      const severityOrder = { critical: 3, major: 2, minor: 1 };
      return severityOrder[b.severity] - severityOrder[a.severity];
    });
  }

  /**
   * Calculate optimal font size for text in given space
   */
  static calculateOptimalFontSize(
    text: string,
    availableSpace: Rectangle,
    maxLines: number = 3,
    fontFamily: string = 'Space Grotesk'
  ): { fontSize: number; lines: number; quality: RenderQuality } {
    const metrics = this.FONT_METRICS[fontFamily as keyof typeof this.FONT_METRICS] || this.FONT_METRICS['Space Grotesk'];
    
    // Start with maximum possible font size
    let fontSize = Math.min(
      availableSpace.height / (maxLines * metrics.heightRatio),
      availableSpace.width / (text.length * metrics.widthRatio)
    );

    // Ensure minimum readable size
    fontSize = Math.max(fontSize, this.QUALITY_THRESHOLDS.readability.minFontSize);

    // Calculate actual lines needed
    const actualLines = this.calculateTextLines(text, availableSpace.width, fontSize, fontFamily);
    const fitsInLines = actualLines <= maxLines;
    const fitsInHeight = actualLines * fontSize * metrics.heightRatio <= availableSpace.height;

    if (!fitsInLines || !fitsInHeight) {
      // Recalculate with constraint
      fontSize = Math.min(
        availableSpace.height / (maxLines * metrics.heightRatio),
        fontSize
      );
    }

    const quality = this.assessTextFitQuality(text, availableSpace, fontSize, actualLines, maxLines);

    return {
      fontSize: Math.max(fontSize, this.QUALITY_THRESHOLDS.readability.minFontSize),
      lines: Math.min(actualLines, maxLines),
      quality
    };
  }

  /**
   * Calculate text lines with word wrapping
   */
  static calculateTextLines(
    text: string,
    maxWidth: number,
    fontSize: number,
    fontFamily: string = 'Space Grotesk'
  ): number {
    if (!text || text.length === 0) return 1;

    const metrics = this.FONT_METRICS[fontFamily as keyof typeof this.FONT_METRICS] || this.FONT_METRICS['Space Grotesk'];
    const charWidth = fontSize * metrics.widthRatio;
    const charsPerLine = Math.floor(maxWidth / charWidth);
    
    if (charsPerLine <= 0) return Math.ceil(text.length); // Each character is a line

    // Simple word wrapping approximation
    const words = text.split(/\s+/);
    let lines = 1;
    let currentLineLength = 0;

    for (const word of words) {
      const wordLength = word.length + 1; // +1 for space
      
      if (currentLineLength + wordLength > charsPerLine) {
        lines++;
        currentLineLength = word.length;
      } else {
        currentLineLength += wordLength;
      }
    }

    return lines;
  }

  /**
   * Test if text fits within constraints
   */
  static testTextFit(
    text: string,
    container: Rectangle,
    fontSize: number,
    maxLines: number = 3,
    fontFamily: string = 'Space Grotesk'
  ): RenderQuality {
    const lines = this.calculateTextLines(text, container.width, fontSize, fontFamily);
    const metrics = this.FONT_METRICS[fontFamily as keyof typeof this.FONT_METRICS] || this.FONT_METRICS['Space Grotesk'];
    const requiredHeight = lines * fontSize * metrics.heightRatio;
    
    const fitsWidth = true; // Word wrapping handles width
    const fitsHeight = requiredHeight <= container.height;
    const fitsLines = lines <= maxLines;
    const fits = fitsWidth && fitsHeight && fitsLines;

    // Quality scoring
    const readabilityScore = Math.min(fontSize / this.QUALITY_THRESHOLDS.readability.comfortableFontSize, 1);
    const spaciousnessScore = fits ? Math.min(container.height / requiredHeight, 1) : 0;
    const aestheticScore = fits ? (lines <= 2 ? 1 : 0.8) : 0.2;
    const informationRetention = fits ? 1 : Math.max(0.2, maxLines / lines);

    return {
      fits,
      readabilityScore,
      aestheticScore,
      informationRetention,
      overallScore: fits ? 
        (readabilityScore * 0.4 + spaciousnessScore * 0.3 + aestheticScore * 0.3) :
        (informationRetention * 0.3 + readabilityScore * 0.2)
    };
  }

  /**
   * Private helper methods
   */
  private static applyScale(rect: Rectangle, scale: number): Rectangle {
    return {
      x: rect.x * scale,
      y: rect.y * scale,
      width: rect.width * scale,
      height: rect.height * scale
    };
  }

  private static calculateEffectivePadding(element: SmartElement, globalScale: number) {
    const basePadding = element.constraints.padding;
    const scale = Math.max(0.5, Math.min(2, globalScale)); // Limit padding scale
    
    return {
      top: basePadding.top * scale,
      right: basePadding.right * scale,
      bottom: basePadding.bottom * scale,
      left: basePadding.left * scale
    };
  }

  private static calculateLocalScale(element: SmartElement, availableSpace: Rectangle): number {
    // Calculate how much the element needs to scale to fit its content
    const originalBounds = element.bounds;
    const scaleX = availableSpace.width / originalBounds.width;
    const scaleY = availableSpace.height / originalBounds.height;
    
    return Math.min(scaleX, scaleY);
  }

  private static calculateFontScale(availableSpace: Rectangle, text: string): number {
    // Estimate appropriate font scale based on available space and text length
    const baseArea = availableSpace.width * availableSpace.height;
    const textDensity = text.length / Math.max(baseArea / 100, 1); // Characters per 100px²
    
    if (textDensity > 2) return 0.8; // Dense text, smaller font
    if (textDensity < 0.5) return 1.2; // Sparse text, larger font
    return 1.0; // Normal density
  }

  private static assessSpaceQuality(
    availableSpace: Rectangle,
    element: SmartElement,
    scaling: any
  ) {
    const area = availableSpace.width * availableSpace.height;
    const textLength = element.content.text?.length || 0;
    const areaPerChar = textLength > 0 ? area / textLength : area;
    
    const spaciousness = Math.min(areaPerChar / 50, 1); // 50px² per character is comfortable
    
    const readability = scaling.effectiveFontScale >= 0.8 ? 
      Math.min(scaling.effectiveFontScale, 1) : 
      scaling.effectiveFontScale * 0.5;
    
    const aesthetics = (spaciousness + readability) / 2;

    return { spaciousness, readability, aesthetics };
  }

  private static generateRecommendations(
    availableSpace: Rectangle,
    element: SmartElement,
    scaling: any
  ) {
    const text = element.content.text || '';
    const maxLines = element.constraints.maxLines;
    
    const optimal = this.calculateOptimalFontSize(text, availableSpace, maxLines);
    
    return {
      optimalFontSize: optimal.fontSize,
      maxLines: optimal.lines,
      suggestedPadding: Math.max(2, Math.min(10, availableSpace.width * 0.05))
    };
  }

  private static checkElementConflict(
    elementA: SmartElement,
    elementB: SmartElement,
    globalScale: number
  ): LayoutConflict | null {
    const boundsA = this.applyScale(elementA.bounds, globalScale);
    const boundsB = this.applyScale(elementB.bounds, globalScale);
    
    const overlapArea = this.calculateOverlap(boundsA, boundsB);
    
    if (overlapArea <= 0) return null;

    const totalAreaA = boundsA.width * boundsA.height;
    const totalAreaB = boundsB.width * boundsB.height;
    const overlapRatio = overlapArea / Math.min(totalAreaA, totalAreaB);

    let severity: 'minor' | 'major' | 'critical' = 'minor';
    if (overlapRatio > 0.5) severity = 'critical';
    else if (overlapRatio > 0.2) severity = 'major';

    let resolution: 'resize' | 'reposition' | 'hide' | 'external-label' = 'reposition';
    if (severity === 'critical') {
      if (elementA.content.priority === 'optional' || elementB.content.priority === 'optional') {
        resolution = 'hide';
      } else {
        resolution = 'external-label';
      }
    }

    return {
      elementA: elementA.id,
      elementB: elementB.id,
      overlapArea,
      severity,
      resolution
    };
  }

  private static calculateOverlap(rectA: Rectangle, rectB: Rectangle): number {
    const left = Math.max(rectA.x, rectB.x);
    const right = Math.min(rectA.x + rectA.width, rectB.x + rectB.width);
    const top = Math.max(rectA.y, rectB.y);
    const bottom = Math.min(rectA.y + rectA.height, rectB.y + rectB.height);

    if (right <= left || bottom <= top) return 0;

    return (right - left) * (bottom - top);
  }

  private static assessTextFitQuality(
    text: string,
    availableSpace: Rectangle,
    fontSize: number,
    actualLines: number,
    maxLines: number
  ): RenderQuality {
    return this.testTextFit(text, availableSpace, fontSize, maxLines);
  }
}